import { useMemo, useState } from 'react'

const isNaNType = (value: unknown) => typeof value === 'number' && isNaN(value)

const isSame = (valueA: unknown, valueB: unknown): boolean => {
    if (typeof valueA !== typeof valueB) return false
    if (isNaNType(valueA) && isNaNType(valueB)) return true
    if (valueA === valueB) return true
    if (typeof valueA !== 'object') return false
    if (valueA === null || valueB === null) return false

    if (Array.isArray(valueA)) {
        if (!Array.isArray(valueB)) return false
        if (valueA.length !== valueB.length) return false
        return valueA.every((v, index) => isSame(v, valueB[index]))
    }

    const keysA = Object.keys(valueA)
    const keysB = Object.keys(valueB as Record<string, unknown>)
    if (keysA.length !== keysB.length) return false
    if (keysA.some((key) => !keysB.includes(key))) return false
    return keysA.every((key) => isSame(valueA[key], valueB[key]))
}

const selectDifferentProperties = <PatchType extends Record<string, unknown>>(
    origin: Record<string, unknown> | undefined,
    patch: PatchType
): Partial<PatchType> => {
    return Object.keys(patch).reduce((diff, key) => {
        return origin && key in origin && isSame(patch[key], origin[key])
            ? diff
            : Object.assign(diff, { [key]: patch[key] })
    }, {})
}

export interface PatchFunction<Type> {
    (changes: Partial<Type>, diff: Partial<Type>, origin: Type): Partial<Type>
}

export interface Patch<Type extends Record<string, unknown>> {
    diff: Partial<Type>
    value: Type
    apply: (changes: Partial<Type>) => void
    set: (changes: Partial<Type>) => void
    reset: () => void
    hasChanges: boolean
}

export default function usePatch<Type extends Record<string, unknown>>(
    origin: Type,
    patch?: PatchFunction<Type>
): Patch<Type> {
    const [diff, setDiff] = useState<Partial<Type>>({})

    return useMemo(
        () => ({
            diff,
            value: { ...origin, ...diff },
            apply: (changes: Partial<Type>) => {
                const newDiff = patch
                    ? patch(changes, diff, origin)
                    : selectDifferentProperties(origin, { ...diff, ...changes })
                setDiff(newDiff)
            },
            set: (changes: Partial<Type>) => {
                const newDiff = patch ? patch(changes, {}, origin) : selectDifferentProperties(origin, changes)
                setDiff(newDiff)
            },
            reset: () => setDiff({}),
            hasChanges: Boolean(Object.keys(diff).length),
        }),
        [origin, diff, setDiff]
    )
}
